import { PERMISSIONS } from '@/utils/permissions'
import { WHATSAPP_FEATURE } from '@/services/planUsage.service'
import type { GateId } from '@/types/milestones'

export interface NavItem {
  id: string
  labelKey: string
  path?: string
  routeName?: string
  permission?: string | string[]
  kind?: 'heading' | 'link'
  children?: NavItem[]
  /**
   * Navigation gate — the item stays visible but locked until the tenant's own
   * documents satisfy the gate. Only set this on leaf items: an ungated parent
   * inherits nothing, so gating a parent would wrongly lock exempt children.
   */
  gate?: GateId
  /** i18n key for lock tooltip (milestones.hints.*). */
  gateHintKey?: string
  /**
   * Add-on entitlement key (e.g. `whatsapp`). The item stays visible but locked
   * until the tenant's plan snapshot reports the feature as `true`; the lock hint
   * is derived at annotation time (featureLocked.<feature>.navHint).
   */
  feature?: string
}

export interface NavSection {
  id: string
  labelKey?: string
  items: NavItem[]
}

export interface QuickActionDef {
  id: string
  labelKey: string
  route: string
  permission: string
  color: string
  iconId: 'plus' | 'document-plus' | 'document-text' | 'user-plus' | 'chart-bar'
}

export interface DashboardTile {
  id: string
  labelKey: string
  descriptionKey?: string
  path: string
  permission?: string
  /** Add-on entitlement key — the tile is hidden when the feature is disabled. */
  feature?: string
}

export interface AdminTileGroup {
  id: string
  titleKey: string
  tiles: DashboardTile[]
}

export interface AdminDashboardSection {
  id: string
  titleKey: string
  tiles?: DashboardTile[]
  groups?: AdminTileGroup[]
}

/** Sidebar navigation — single source of truth (labels via i18n keys). */
export const sidebarSections: NavSection[] = [
  {
    id: 'dashboard',
    items: [{ id: 'dashboard', labelKey: 'nav.dashboard', path: '/', permission: PERMISSIONS.DASHBOARD_VIEW }],
  },
  {
    id: 'crm',
    labelKey: 'nav.sections.crm',
    items: [
      { id: 'crm-contacts', labelKey: 'nav.crmContacts', path: '/crm/contacts', permission: PERMISSIONS.LEADS },
      { id: 'leads', labelKey: 'nav.leads', path: '/leads', permission: PERMISSIONS.LEADS },
    ],
  },
  {
    id: 'selling',
    labelKey: 'nav.sections.selling',
    items: [
      {
        id: 'customers',
        labelKey: 'nav.customersList',
        path: '/customers',
        permission: PERMISSIONS.CLIENTS,
      },
      {
        id: 'quotations',
        labelKey: 'nav.quotations',
        permission: PERMISSIONS.QUOTATIONS,
        children: [
          { id: 'quotations-new', labelKey: 'nav.addQuotation', path: '/quotations/new', permission: PERMISSIONS.QUOTATIONS_CREATE, gate: 'quotations', gateHintKey: 'milestones.hints.quotations' },
          { id: 'quotations-list', labelKey: 'nav.quotationsList', path: '/quotations', gate: 'quotations', gateHintKey: 'milestones.hints.quotations' },
        ],
      },
      {
        id: 'contracts',
        labelKey: 'nav.contracts',
        permission: PERMISSIONS.CONTRACTS,
        children: [
          { id: 'contracts-new', labelKey: 'nav.addContract', path: '/contracts/new', permission: PERMISSIONS.CONTRACTS_CREATE, gate: 'contracts', gateHintKey: 'milestones.hints.contracts' },
          { id: 'contracts-dashboard', labelKey: 'nav.myContractsDashboard', path: '/contracts/my-dashboard', gate: 'contracts', gateHintKey: 'milestones.hints.contracts' },
          { id: 'contracts-list', labelKey: 'nav.contractsList', path: '/contracts', gate: 'contracts', gateHintKey: 'milestones.hints.contracts' },
        ],
      },
      {
        id: 'sales-orders',
        labelKey: 'nav.salesOrders',
        permission: PERMISSIONS.SALES_ORDERS,
        children: [
          { id: 'sales-orders-new', labelKey: 'nav.addSalesOrder', path: '/sales-orders/new', permission: PERMISSIONS.SALES_ORDERS_CREATE, gate: 'sales_orders', gateHintKey: 'milestones.hints.sales_orders' },
          { id: 'sales-orders-list', labelKey: 'nav.salesOrdersList', path: '/sales-orders', gate: 'sales_orders', gateHintKey: 'milestones.hints.sales_orders' },
        ],
      },
    ],
  },
  {
    id: 'projects',
    labelKey: 'nav.sections.projects',
    items: [
      {
        id: 'projects',
        labelKey: 'nav.projects',
        permission: PERMISSIONS.PROJECTS,
        children: [
          { id: 'projects-new', labelKey: 'nav.addProject', path: '/projects/new', permission: PERMISSIONS.PROJECTS_CREATE, gate: 'projects', gateHintKey: 'milestones.hints.projects' },
          { id: 'projects-list', labelKey: 'nav.projectsList', path: '/projects', gate: 'projects', gateHintKey: 'milestones.hints.projects' },
        ],
      },
    ],
  },
  {
    id: 'inventory',
    labelKey: 'nav.sections.inventory',
    items: [
      {
        id: 'items',
        labelKey: 'nav.items',
        permission: PERMISSIONS.ITEMS_VIEW,
        children: [
          { id: 'items-new', labelKey: 'nav.addItem', path: '/items/new', permission: PERMISSIONS.ITEMS_CREATE },
          { id: 'items-list', labelKey: 'nav.itemsList', path: '/items' },
        ],
      },
      {
        id: 'delivery-notes',
        labelKey: 'nav.deliveryNotes',
        permission: PERMISSIONS.DELIVERY_NOTES_VIEW,
        children: [
          { id: 'delivery-notes-new', labelKey: 'nav.addDeliveryNote', path: '/delivery-notes/new', permission: PERMISSIONS.DELIVERY_NOTES_CREATE, gate: 'delivery_notes', gateHintKey: 'milestones.hints.delivery_notes' },
          { id: 'delivery-notes-list', labelKey: 'nav.deliveryNotesList', path: '/delivery-notes', gate: 'delivery_notes', gateHintKey: 'milestones.hints.delivery_notes' },
        ],
      },
    ],
  },
  {
    id: 'accounting',
    labelKey: 'nav.sections.accounting',
    items: [
      {
        id: 'invoices',
        labelKey: 'nav.invoices',
        permission: PERMISSIONS.INVOICES_VIEW,
        children: [
          { id: 'invoices-new', labelKey: 'nav.addInvoice', path: '/invoices/new', permission: PERMISSIONS.INVOICES_CREATE, gate: 'invoices', gateHintKey: 'milestones.hints.invoices' },
          { id: 'invoices-list', labelKey: 'nav.invoicesList', path: '/invoices', gate: 'invoices', gateHintKey: 'milestones.hints.invoices' },
          { id: 'credit-notes-new', labelKey: 'nav.addCreditNote', path: '/credit-notes/new', permission: PERMISSIONS.CREDIT_NOTES_CREATE },
          { id: 'credit-notes-list', labelKey: 'nav.creditNotesList', path: '/credit-notes', permission: PERMISSIONS.CREDIT_NOTES_VIEW },
          { id: 'receipt-inbox', labelKey: 'nav.receiptInbox', path: '/receipt-inbox', permission: PERMISSIONS.RECEIPT_INBOX_READ, gate: 'invoices', gateHintKey: 'milestones.hints.invoices' },
        ],
      },
      {
        id: 'ledger',
        labelKey: 'nav.ledger',
        permission: PERMISSIONS.ACCOUNTING_VIEW,
        children: [
          { id: 'accounting-dashboard', labelKey: 'nav.accountingDashboard', path: '/accounting' },
          { id: 'chart-of-accounts', labelKey: 'nav.chartOfAccounts', path: '/accounting/chart-of-accounts' },
          { id: 'journal-entries', labelKey: 'nav.journalEntries', path: '/accounting/journals', permission: PERMISSIONS.JOURNAL_LIST },
          { id: 'fiscal-periods', labelKey: 'nav.fiscalPeriods', path: '/accounting/fiscal', permission: PERMISSIONS.FISCAL_YEAR_READ },
          { id: 'fx-revaluation', labelKey: 'nav.fxRevaluation', path: '/accounting/fx', permission: PERMISSIONS.FISCAL_YEAR_READ },
        ],
      },
      {
        id: 'payables',
        labelKey: 'nav.payables',
        children: [
          { id: 'suppliers', labelKey: 'nav.suppliers', path: '/accounting/suppliers', permission: PERMISSIONS.SUPPLIER_LIST },
          { id: 'bills', labelKey: 'nav.bills', path: '/accounting/bills', permission: PERMISSIONS.BILL_LIST, gate: 'bills', gateHintKey: 'milestones.hints.bills' },
        ],
      },
      {
        id: 'cash',
        labelKey: 'nav.cash',
        children: [
          { id: 'treasuries', labelKey: 'nav.treasuries', path: '/accounting/treasuries', permission: PERMISSIONS.TREASURY_LIST },
          { id: 'payment-entries', labelKey: 'nav.paymentEntries', path: '/accounting/payments', permission: PERMISSIONS.PAYMENT_ENTRY_LIST, gate: 'payments', gateHintKey: 'milestones.hints.payments' },
          { id: 'banking', labelKey: 'nav.banking', path: '/accounting/banking', permission: PERMISSIONS.BANK_READ, gate: 'payments', gateHintKey: 'milestones.hints.payments' },
        ],
      },
      {
        id: 'accounting-reports',
        labelKey: 'nav.reports',
        permission: PERMISSIONS.ACCOUNTING_REPORTS,
        children: [
          { id: 'accounting-reports-list', labelKey: 'nav.accountingReports', path: '/accounting/reports', permission: PERMISSIONS.ACCOUNTING_REPORTS, gate: 'accounting_reports', gateHintKey: 'milestones.hints.accounting_reports' },
          { id: 'ledger-integrity', labelKey: 'nav.ledgerIntegrity', path: '/accounting/integrity', permission: PERMISSIONS.ACCOUNTING_REPORTS },
          { id: 'dunning', labelKey: 'nav.dunning', path: '/accounting/dunning', permission: PERMISSIONS.ACCOUNTING_REPORTS },
          { id: 'opening-invoices', labelKey: 'nav.openingInvoices', path: '/accounting/opening-invoices', permission: PERMISSIONS.ACCOUNTING_REPORTS },
        ],
      },
      {
        id: 'accounting-setup',
        labelKey: 'nav.setup',
        children: [
          { id: 'tax-codes', labelKey: 'nav.taxCodes', path: '/accounting/tax-codes', permission: PERMISSIONS.TAX_CODE_LIST },
          { id: 'tax-withholding', labelKey: 'nav.taxWithholding', path: '/accounting/withholding', permission: PERMISSIONS.TAX_CODE_LIST },
          { id: 'payment-terms', labelKey: 'nav.paymentTerms', path: '/accounting/payment-terms', permission: PERMISSIONS.PAYMENT_TERM_READ },
          { id: 'payment-methods', labelKey: 'nav.paymentMethods', path: '/settings/payment-methods', permission: PERMISSIONS.SETTINGS_READ },
          { id: 'accounting-settings', labelKey: 'nav.accountingSettings', path: '/accounting/settings', permission: PERMISSIONS.ACCOUNTING_SETTINGS_UPDATE },
          { id: 'cost-centers', labelKey: 'nav.costCenters', path: '/accounting/cost-centers', permission: PERMISSIONS.ACCOUNTING_VIEW },
          { id: 'currencies', labelKey: 'nav.currencies', path: '/accounting/currencies', permission: PERMISSIONS.ACCOUNTING_VIEW },
        ],
      },
    ],
  },
  {
    id: 'communication',
    labelKey: 'nav.sections.communication',
    items: [
      {
        id: 'whatsapp-inbox',
        labelKey: 'nav.whatsappInbox',
        path: '/whatsapp',
        permission: PERMISSIONS.WHATSAPP_READ,
        feature: WHATSAPP_FEATURE,
      },
      {
        id: 'whatsapp-document-requests',
        labelKey: 'nav.whatsappDocumentRequests',
        path: '/whatsapp/document-requests',
        permission: PERMISSIONS.WHATSAPP_READ,
        feature: WHATSAPP_FEATURE,
      },
    ],
  },
  {
    id: 'preferences',
    items: [
      { id: 'support', labelKey: 'nav.support', path: '/support/tickets', permission: PERMISSIONS.SUPPORT_READ },
      { id: 'settings', labelKey: 'common.settings', path: '/settings' },
      { id: 'admin-dashboard', labelKey: 'nav.adminDashboard', path: '/admin', permission: PERMISSIONS.ADMIN },
    ],
  },
]

export const quickActions: QuickActionDef[] = [
  {
    id: 'create-project',
    labelKey: 'dashboard.createProject',
    route: '/projects/new',
    permission: PERMISSIONS.PROJECTS_CREATE,
    color: 'bg-primary-500',
    iconId: 'plus',
  },
  {
    id: 'create-quotation',
    labelKey: 'dashboard.createQuotation',
    route: '/quotations/new',
    permission: PERMISSIONS.QUOTATIONS_CREATE,
    color: 'bg-green-500',
    iconId: 'document-plus',
  },
  {
    id: 'create-contract',
    labelKey: 'dashboard.createContract',
    route: '/contracts/new',
    permission: PERMISSIONS.CONTRACTS_CREATE,
    color: 'bg-purple-500',
    iconId: 'document-text',
  },
  {
    id: 'add-client',
    labelKey: 'dashboard.addClient',
    route: '/customers',
    permission: PERMISSIONS.CLIENTS_CREATE,
    color: 'bg-primary-500',
    iconId: 'user-plus',
  },
  {
    id: 'view-reports',
    labelKey: 'dashboard.viewReports',
    route: '/reports',
    permission: PERMISSIONS.REPORTS,
    color: 'bg-orange-500',
    iconId: 'chart-bar',
  },
]

export const accountingDashboardTiles: DashboardTile[] = [
  { id: 'coa', labelKey: 'accounting.chartOfAccounts', path: '/accounting/chart-of-accounts', permission: PERMISSIONS.ACCOUNTING_VIEW },
  { id: 'journals', labelKey: 'accounting.journalEntries', path: '/accounting/journals', permission: PERMISSIONS.JOURNAL_LIST },
  { id: 'reports', labelKey: 'accounting.reports', path: '/accounting/reports', permission: PERMISSIONS.ACCOUNTING_REPORTS },
  { id: 'suppliers', labelKey: 'accounting.suppliers', path: '/accounting/suppliers', permission: PERMISSIONS.SUPPLIER_LIST },
  { id: 'bills', labelKey: 'accounting.bills', path: '/accounting/bills', permission: PERMISSIONS.BILL_LIST },
  { id: 'payments', labelKey: 'accounting.paymentEntries', path: '/accounting/payments', permission: PERMISSIONS.PAYMENT_ENTRY_LIST },
  { id: 'tax-codes', labelKey: 'accounting.taxCodes', path: '/accounting/tax-codes', permission: PERMISSIONS.TAX_CODE_LIST },
  { id: 'fiscal', labelKey: 'accounting.fiscalPeriods', path: '/accounting/fiscal', permission: PERMISSIONS.FISCAL_YEAR_READ },
  { id: 'erpnext', labelKey: 'accounting.erpnextSync', path: '/accounting/erpnext', permission: PERMISSIONS.ERPNEXT_READ },
]

export const adminDashboardSections: AdminDashboardSection[] = [
  {
    id: 'integrations',
    titleKey: 'admin.sectionIntegrations',
    tiles: [
      {
        id: 'branding',
        labelKey: 'branding.title',
        descriptionKey: 'branding.adminTileDesc',
        path: '/admin/branding',
        permission: PERMISSIONS.SETTINGS_READ,
      },
      {
        id: 'template-studio',
        labelKey: 'templateStudio.title',
        descriptionKey: 'templateStudio.settingsBlurb',
        path: '/settings/template-studio',
        permission: PERMISSIONS.TEMPLATE_READ,
      },
      {
        id: 'document-serials',
        labelKey: 'settings.documentSerials.title',
        descriptionKey: 'settings.documentSerials.blurb',
        path: '/settings/document-serials',
        permission: PERMISSIONS.SERIAL_READ,
      },
      {
        id: 'background-jobs',
        labelKey: 'admin.jobs.title',
        descriptionKey: 'admin.jobs.tileDesc',
        path: '/admin/jobs',
        permission: PERMISSIONS.JOBS_READ,
      },
    ],
    groups: [
      {
        id: 'external-invoicing',
        titleKey: 'admin.sectionExternalInvoicing',
        tiles: [
          {
            id: 'daftra',
            labelKey: 'nav.daftra',
            descriptionKey: 'daftra.settings.blurb',
            path: '/settings/daftra',
            permission: PERMISSIONS.DAFTRA,
          },
          {
            id: 'erpnext',
            labelKey: 'nav.erpnext',
            descriptionKey: 'erpnext.settings.blurb',
            path: '/settings/erpnext',
            permission: PERMISSIONS.ERPNEXT_SETTINGS,
          },
        ],
      },
      {
        id: 'messaging',
        titleKey: 'admin.sectionMessaging',
        tiles: [
          {
            id: 'whatsapp',
            labelKey: 'nav.whatsappSettings',
            descriptionKey: 'whatsapp.settings.adminTileDesc',
            path: '/settings/whatsapp',
            permission: PERMISSIONS.WHATSAPP_MANAGE,
            feature: WHATSAPP_FEATURE,
          },
        ],
      },
    ],
  },
  {
    id: 'access',
    titleKey: 'admin.sectionAccessControl',
    tiles: [
      {
        id: 'access-control',
        labelKey: 'admin.sectionAccessControl',
        descriptionKey: 'admin.accessHub.tileDesc',
        path: '/admin/access/users',
        permission: PERMISSIONS.ADMIN_USERS,
      },
    ],
  },
  {
    id: 'org',
    titleKey: 'admin.sectionOrganization',
    tiles: [
      {
        id: 'org-structure',
        labelKey: 'admin.orgStructureTitle',
        descriptionKey: 'admin.orgStructureDesc',
        path: '/admin/org-structure',
        permission: PERMISSIONS.SETTINGS_READ,
      },
      {
        id: 'project-types',
        labelKey: 'admin.projectTypes',
        descriptionKey: 'admin.projectTypesDesc',
        path: '/admin/project-types',
        permission: PERMISSIONS.PROJECT_TYPES_MANAGE,
      },
      {
        id: 'file-types',
        labelKey: 'admin.fileTypes',
        descriptionKey: 'admin.fileTypesDesc',
        path: '/admin/file-types',
        permission: PERMISSIONS.FILE_TYPES_MANAGE,
      },
      {
        id: 'branches',
        labelKey: 'branches.title',
        descriptionKey: 'branches.description',
        path: '/admin/branches',
        permission: PERMISSIONS.BRANCH_READ,
      },
    ],
  },
]

/** Settings sub-links gated by integration/admin permissions. */
export const settingsLinks: DashboardTile[] = [
  {
    id: 'branches',
    labelKey: 'branches.title',
    path: '/admin/branches',
    permission: PERMISSIONS.BRANCH_READ,
  },
  {
    id: 'project-types',
    labelKey: 'admin.projectTypes',
    path: '/admin/project-types',
    permission: PERMISSIONS.PROJECT_TYPES_MANAGE,
  },
  {
    id: 'file-types',
    labelKey: 'admin.fileTypes',
    path: '/admin/file-types',
    permission: PERMISSIONS.FILE_TYPES_MANAGE,
  },
  {
    id: 'document-serials',
    labelKey: 'settings.documentSerials.openSettings',
    path: '/settings/document-serials',
    permission: PERMISSIONS.SERIAL_READ,
  },
  {
    id: 'payment-methods',
    labelKey: 'settings.paymentMethods.title',
    path: '/settings/payment-methods',
    permission: PERMISSIONS.SETTINGS_READ,
  },
  {
    id: 'template-studio',
    labelKey: 'templateStudio.title',
    path: '/settings/template-studio',
    permission: PERMISSIONS.TEMPLATE_READ,
  },
  {
    id: 'daftra',
    labelKey: 'nav.daftra',
    path: '/settings/daftra',
    permission: PERMISSIONS.DAFTRA,
  },
  {
    id: 'erpnext',
    labelKey: 'nav.erpnext',
    path: '/settings/erpnext',
    permission: PERMISSIONS.ERPNEXT_SETTINGS,
  },
  {
    id: 'whatsapp',
    labelKey: 'nav.whatsappSettings',
    path: '/settings/whatsapp',
    permission: PERMISSIONS.WHATSAPP_MANAGE,
    feature: WHATSAPP_FEATURE,
  },
]

/** Authenticated routes intentionally without meta.permission (auth-only UX). */
export const AUTH_ONLY_ROUTE_NAMES = new Set([
  'Dashboard',
  'ChangePassword',
  'Profile',
  'PointHistory',
  'Withdrawals',
  'PointConversions',
  'UserLevel',
  'UserStreak',
  'Settings',
  'ShareTarget',
  'PaymentScheduleLink',
  'NotificationsList',
  'Forbidden',
])
